<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { canSendSms } from '@Constants';
import { emailValidation, phoneValidation } from '@FormModule';

interface SubmitDetail {
  checkoutFlow: string;
  deviceCheckoutMethod: string;
  email: string;
  phone: string;
}

const props = defineProps<{
  people: unknown;
  submitLoading: boolean;
}>();

const emit = defineEmits<{
  onSubmit: [detail: SubmitDetail];
}>();

const visible = defineModel<boolean>('visible', { required: true });

const deviceCheckoutMethods = [
  { icon: 'faWindowMaximize', label: 'cart.mobileCheckout.openTab', value: 'newTab' },
  { icon: 'faQrcode', label: 'cart.mobileCheckout.getQrCode', value: 'qrCode' },
  { icon: 'faLink', label: 'cart.mobileCheckout.sendLink', value: 'link' },
];

const adopterEmail = ref('');
const adopterEmailError = ref('');
const adopterPhone = ref('');
const adopterPhoneError = ref('');
const checkoutMethod = ref<string>();
const deviceCheckoutMethod = ref('');
const showMobileCheckoutError = ref(false);

// NOTE :: Countries off the SMS list get the email path only; the api refuses a phone for them.
const isSmsAvailable = computed(() => canSendSms());

const checkoutMethods = computed(() => [
  { icon: 'faMobile', label: 'cart.equipmentRentalMobileCheckout.sendLinkToRenter', value: 'adopter' },
  { icon: 'faHome', label: 'cart.equipmentRentalMobileCheckout.checkoutOnShelterDevice', value: 'shelter' },
]);

const showCheckoutLinkError = computed(
  () =>
    (checkoutMethod.value === 'adopter' && !adopterEmail.value && !adopterPhone.value) ||
    !!adopterPhoneError.value ||
    !!adopterEmailError.value
);

const shelterCheckoutByLinkHasError = computed(() => deviceCheckoutMethod.value === 'link' && !adopterEmail.value);

const showShelterCheckoutMethod = computed(
  () => (checkoutMethod.value === 'shelter' && !deviceCheckoutMethod.value) || shelterCheckoutByLinkHasError.value
);

watch(visible, onDialogVisibilityChange);
watch(checkoutMethod, onCheckoutMethodChange);
watch(deviceCheckoutMethod, onDeviceCheckoutMethodFieldChange);
watch(adopterEmail, onAdopterEmailUpdate);
watch(adopterPhone, onAdopterPhoneUpdate);

function resetFields() {
  checkoutMethod.value = undefined;
  deviceCheckoutMethod.value = '';
  adopterEmail.value = '';
  adopterPhone.value = '';
  adopterEmailError.value = '';
  adopterPhoneError.value = '';
  showMobileCheckoutError.value = false;
}

function onCheckoutMethodChange() {
  adopterEmail.value = '';
  adopterPhone.value = '';
  adopterEmailError.value = '';
  adopterPhoneError.value = '';
  showMobileCheckoutError.value = false;
  deviceCheckoutMethod.value = '';

  if (checkoutMethod.value === 'adopter' && props.people) {
    const person = Array.isArray(props.people) ? props.people[0] : props.people;
    if (!person) return;

    if (person.email) adopterEmail.value = person.email;

    const canPrefillPhone = !!person.phone && isSmsAvailable.value;
    if (canPrefillPhone) adopterPhone.value = person.phone;
  }
}

function onAdopterEmailUpdate() {
  showMobileCheckoutError.value = false;
  if (!adopterEmail.value) return (adopterEmailError.value = '');

  const notValidEmail = !emailValidation(adopterEmail.value);
  if (notValidEmail) return (adopterEmailError.value = 'components.forms.errors.validEmail');

  adopterEmailError.value = '';
}

function onAdopterPhoneUpdate() {
  showMobileCheckoutError.value = false;
  if (!adopterPhone.value) return (adopterPhoneError.value = '');

  const notValidPhone = !phoneValidation(adopterPhone.value);
  if (notValidPhone) return (adopterPhoneError.value = 'components.forms.errors.validPhone');

  adopterPhoneError.value = '';
}

function onSubmit() {
  const hasError = !checkoutMethod.value || showCheckoutLinkError.value || showShelterCheckoutMethod.value;
  if (hasError) return (showMobileCheckoutError.value = true);

  showMobileCheckoutError.value = false;
  emit('onSubmit', {
    checkoutFlow: checkoutMethod.value as string,
    deviceCheckoutMethod: deviceCheckoutMethod.value,
    email: adopterEmail.value,
    phone: adopterPhone.value,
  });
}

function onDialogVisibilityChange(isOpen: boolean) {
  if (isOpen) {
    resetFields();

    return;
  }

  showMobileCheckoutError.value = false;
}

function onDeviceCheckoutMethodFieldChange() {
  if (!deviceCheckoutMethod.value) return;

  showMobileCheckoutError.value = false;
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    class="max-h-[90vh] w-full max-w-[560px]"
    pt:title="text-lg font-semibold"
    :header="$t('cart.equipmentRentalMobileCheckout.sendMobileCheckoutLink')"
  >
    <div class="flex max-h-[calc(90vh-8rem)] flex-col gap-4 overflow-y-auto">
      <div class="flex flex-col gap-2">
        <AFieldLabel
          required
          :error="showMobileCheckoutError && !checkoutMethod"
          :label="$t('cart.equipmentRentalMobileCheckout.checkoutOption')"
        />

        <div class="flex flex-wrap gap-1 sm:gap-4">
          <AFieldCheckboxButton
            v-model="checkoutMethod"
            :options="checkoutMethods"
          />
        </div>
      </div>

      <div
        v-if="checkoutMethod === 'adopter'"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          :error="showMobileCheckoutError && showCheckoutLinkError"
          :label="$t('cart.mobileCheckout.deliveryMethod')"
        />

        <div class="flex w-full max-w-md flex-col justify-center gap-1">
          <template v-if="isSmsAvailable">
            <div class="flex flex-col items-start">
              <AFieldPhone
                v-model="adopterPhone"
                :error="(showMobileCheckoutError && showCheckoutLinkError) || (adopterPhoneError && !adopterEmail)"
                :placeholder="$t('cart.mobileCheckout.enterPhone')"
                @blur="onAdopterPhoneUpdate"
              />

              <AFieldError
                v-if="adopterPhoneError && !adopterEmail"
                :error="adopterPhoneError"
              />
            </div>

            <div class="flex flex-col items-center">
              <span class="text-sm font-semibold">
                {{ $t('common.or') }}
              </span>
            </div>
          </template>

          <form class="flex flex-col items-start">
            <AFieldEmail
              v-model="adopterEmail"
              :error="(showMobileCheckoutError && showCheckoutLinkError) || (adopterEmailError && !adopterPhone)"
              :placeholder="$t('cart.mobileCheckout.enterEmail')"
            />

            <AFieldError
              v-if="adopterEmailError && !adopterPhone"
              :error="adopterEmailError"
            />
          </form>

          <p
            v-if="!isSmsAvailable"
            class="text-sm text-gray-500"
          >
            {{ $t('components.forms.smsNotAvailableInCountry') }}
          </p>
        </div>
      </div>

      <div
        v-if="checkoutMethod === 'shelter'"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          :error="showMobileCheckoutError && showShelterCheckoutMethod"
          :label="$t('cart.mobileCheckout.deviceCheckoutMethod')"
        />

        <div class="flex flex-wrap gap-4">
          <AFieldCheckboxButton
            v-model="deviceCheckoutMethod"
            class="text-nowrap"
            :options="deviceCheckoutMethods"
          />
        </div>

        <div
          v-if="deviceCheckoutMethod === 'link'"
          class="max-w-[500px]"
        >
          <AFieldEmail
            v-model="adopterEmail"
            :error="(showMobileCheckoutError && showShelterCheckoutMethod) || adopterEmailError"
            :placeholder="$t('cart.mobileCheckout.enterEmail')"
          />

          <AFieldError :error="adopterEmailError" />
        </div>
      </div>

      <Message
        v-if="showMobileCheckoutError"
        pt:text="text-sm"
        severity="error"
        :closable="false"
      >
        {{ $t('cart.mobileCheckout.formError') }}
      </Message>
    </div>

    <template #footer>
      <Button
        class="af-button"
        severity="secondary"
        :disabled="submitLoading"
        :label="$t('common.cancel')"
        @click="visible = false"
      />

      <Button
        class="af-button af-button-success"
        iconPos="right"
        :label="$t('cart.label.submitAndComplete')"
        :loading="submitLoading"
        @click="onSubmit"
      >
        <template #icon>
          <FaIcon
            class="mr-2"
            :icon="$icons.faPaw"
          />
        </template>
      </Button>
    </template>
  </Dialog>
</template>
